import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Segment,
  Step,
} from "semantic-ui-react";
import formData from "../data/applicationFormData.json";
import { roomTypeApi, paymentResponsibilityApi } from "../api/api";
import TimeInput12h from "./TimeInput12h";

/* Matches client_max_body_size in the nginx site config and
   DATA_UPLOAD_MAX_MEMORY_SIZE in Django settings — a larger file is rejected
   by nginx with a 413 before the request ever reaches the API. */
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const ATTACHMENT_TOO_LARGE_MSG = "Attachment must be 10 MB or smaller.";

const STEP_ICONS = ["id card outline", "user outline", "calendar outline"];
const GREYED_INPUT_STYLE = { backgroundColor: "#eceff1" };

const paymentModeAliases = {
  Cash: "BY_GUEST",
  Online: "BY_PERSON",
};

const OTHER_PAYMENT_CODE = "OTHER";

const bookingTypeOptions = [
  { key: "official", text: "Official", value: "Other" },
  { key: "personal", text: "Non-official", value: "Personal" },
];

const guestHouseDropdownOptions = formData.guestHouseOptions.map((option) => ({
  key: option.guestHouseName,
  text: option.guestHouseName,
  value: option.guestHouseName,
}));

const guestHousePreferenceLimit = formData.guestHouseOptions.length;
const getFallbackGuestHouseName = () =>
  formData.guestHouseOptions[0].guestHouseName;

const getSelectedPaymentMode = (paymentMode) =>
  paymentModeAliases[paymentMode] || paymentMode || "";

const getPreviewValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  return value?.toString().trim() || "-";
};

const getBookingTypeLabel = (bookingType) =>
  bookingType === "Other" ? "Official" : bookingType === "Personal" ? "Non-official" : getPreviewValue(bookingType);

const getDateParts = (dateValue) => {
  const value = dateValue?.toString().trim();

  if (!value) {
    return null;
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const displayMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);

  const parts = isoMatch
    ? {
        year: Number(isoMatch[1]),
        month: Number(isoMatch[2]),
        day: Number(isoMatch[3]),
      }
    : displayMatch
      ? {
          year: Number(displayMatch[3]),
          month: Number(displayMatch[2]),
          day: Number(displayMatch[1]),
        }
      : null;

  if (!parts) {
    return null;
  }

  const date = new Date(parts.year, parts.month - 1, parts.day);
  const isValidDate =
    date.getFullYear() === parts.year &&
    date.getMonth() === parts.month - 1 &&
    date.getDate() === parts.day;

  return isValidDate ? parts : null;
};

const padDatePart = (part) => part.toString().padStart(2, "0");

const getIsoDateValue = (dateValue) => {
  const parts = getDateParts(dateValue);

  if (!parts) {
    return "";
  }

  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}`;
};

const getDisplayDateValue = (dateValue) => {
  const parts = getDateParts(dateValue);

  if (!parts) {
    return dateValue || "";
  }

  return `${padDatePart(parts.day)}/${padDatePart(parts.month)}/${parts.year}`;
};

/* TimeInput12h stores 24-hour "HH:MM" so it round-trips to the API unchanged —
   the review step reads back to the applicant, so show it the way they picked it. */
const getDisplayTimeValue = (timeValue) => {
  if (!timeValue) return "";
  const [h, m] = String(timeValue).split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return timeValue;
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

const getDateFromValue = (dateValue) => {
  const parts = getDateParts(dateValue);

  return parts ? new Date(parts.year, parts.month - 1, parts.day) : null;
};

const getSafeDocumentTitle = (title) =>
  title
    ?.toString()
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "-") || "guest-house-booking";

const draftStorageKey = "guest-house-accommodation-form-draft";

const hasRequestData = (request) =>
  Boolean(request && Object.keys(request).length);

const getSavedDraftValues = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const savedDraft = window.localStorage.getItem(draftStorageKey);
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch {
    return null;
  }
};

const saveDraftValues = (values) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const draft = {
      ...values,
      /* File objects can't be serialised — store only display metadata */
      officialAttachment: values.officialAttachment
        ? { name: values.officialAttachment.name, size: values.officialAttachment.size, type: values.officialAttachment.type }
        : null,
    };
    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
};

const getEmployeeDocumentName = (values) =>
  getSafeDocumentTitle(values.employeeId || "employee-id");

const getInitialFormValues = (request) =>
  hasRequestData(request)
    ? getFormValuesFromRequest(request)
    : getSavedDraftValues() || getFormValuesFromRequest(request);

const getPreviewPrintMarkup = (previewContent, documentTitle) => `
  <!doctype html>
  <html>
    <head>
      <title>${documentTitle ? getSafeDocumentTitle(documentTitle) : ""}</title>
      <style>
        @page { size: A4; margin: 14mm 12mm; }

        * { box-sizing: border-box; }

        html, body {
          margin: 0;
          padding: 0;
          background: #f0f4f8;
          color: #1a2e40;
          font-family: "Segoe UI", Arial, Helvetica, sans-serif;
          font-size: 10.5px;
          line-height: 1.4;
        }

        /* hide semantic ui icon glyphs that won't load in print window */
        .icon { display: none !important; }

        #booking-preview-content {
          max-width: 720px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 12px 0;
        }

        /* ── Document title (h3.ui.header) ── */
        h3.ui.header {
          font-family: Georgia, "Times New Roman", serif;
          font-size: 13px;
          font-weight: 700;
          color: #143a5a;
          text-align: center;
          margin: 0 0 4px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* ── Section cards (Segment) ── */
        .ui.segment {
          background: #ffffff;
          border: 1px solid #d5e0ec;
          border-radius: 8px;
          padding: 8px 14px;
          box-shadow: none;
          page-break-inside: avoid;
        }
        .ui.segment > h5.ui.header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #143a5a;
          margin: 0 0 6px;
        }
        .ui.label.circular {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #143a5a !important;
          color: #ffffff !important;
          font-size: 7px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* ── Section grid (Grid / Grid.Column) ── */
        .ui.grid {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -7px;
        }
        .ui.grid > .column {
          box-sizing: border-box;
          flex: 0 0 50%;
          max-width: 50%;
          padding: 4px 7px;
          font-size: 10px;
        }
        .ui.grid > .column > div:first-child {
          font-size: 7px;
          color: #7a95ae;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          font-weight: 700;
        }
        .ui.grid > .column > div:last-child {
          color: #1a2e40;
          font-weight: 500;
          word-break: break-word;
        }

      </style>
    </head>
    <body>${previewContent}</body>
  </html>
`;

/* A same-day (day-use) stay spans 0 nights, so report its hours instead —
   the old Math.max(1, ...) displayed a night the guest never books. */
const getStayDaysFromDates = (checkInDate, checkOutDate, checkInTime, checkOutTime) => {
  const startDate = getDateFromValue(checkInDate);
  const endDate = getDateFromValue(checkOutDate);

  if (!startDate || !endDate) return "";

  if (checkInDate === checkOutDate) {
    if (!checkInTime || !checkOutTime) return "";
    const hours =
      (new Date(`${checkOutDate}T${checkOutTime}:00`) - new Date(`${checkInDate}T${checkInTime}:00`)) /
      3600000;
    return hours > 0 ? `Day use (${Math.round(hours * 10) / 10} hrs)` : "";
  }

  const nights = Math.round((endDate - startDate) / 86400000);
  return nights > 0 ? String(nights) : "";
};

const getFormValuesFromRequest = (request = {}) => ({
  guestHouseName:
    request.guestHouseName || formData.guestHouseOptions[0].guestHouseName,
  guestHousePreferences: request.guestHousePreferences?.length
    ? request.guestHousePreferences
    : request.guestHouseName
      ? [request.guestHouseName]
      : [],
  guestName: request.guestName || "",
  guestDesignation: request.guestDesignation || "",
  guestAddress: request.guestAddress || "",
  guestMobileNumber: request.guestMobileNumber || "",
  guestSameAsApplicant: false,
  accompanyingPersonsCount: request.accompanyingPersonsCount || 0,
  roomCount: request.assignedRooms?.length
    ? String(request.assignedRooms.length)
    : request.assignedRoom
      ? "1"
      : request.roomType === "double"
        ? "2"
        : "1",
  visitPurpose: request.purpose || "",
  arrivalDate: request.checkIn || "",
  arrivalTime: request.arrivalTime || "",
  departureDate: request.checkOut || "",
  departureTime: request.departureTime || "",
  numberOfDays:
    request.stayDays ||
    getStayDaysFromDates(
      request.checkIn, request.checkOut, request.arrivalTime, request.departureTime,
    ),
  applicantName: request.applicantName || "",
  employeeId: request.employeeId || "",
  applicantDesignation: request.designation || "",
  applicantDepartment: request.department || "",
  applicantMobileNumber: request.phone || "",
  emailId: request.email || "",
  paymentMode: getSelectedPaymentMode(request.modeOfPayment),
  paymentModeOther: request.modeOfPaymentOther || "",
  bookingType: request.bookingType || "Other",
  officialAttachment: request.officialAttachment || null,
  roomType: request.roomType || "single",
  roomRequestsByPreference: request.roomRequestsByPreference || {},
});

const getClearedFormValues = () => ({
  guestHouseName: "",
  guestHousePreferences: [],
  guestName: "",
  guestDesignation: "",
  guestAddress: "",
  guestMobileNumber: "",
  guestSameAsApplicant: false,
  accompanyingPersonsCount: 0,
  roomCount: "",
  visitPurpose: "",
  arrivalDate: "",
  arrivalTime: "",
  departureDate: "",
  departureTime: "",
  numberOfDays: "",
  applicantName: "",
  employeeId: "",
  applicantDesignation: "",
  applicantDepartment: "",
  applicantMobileNumber: "",
  emailId: "",
  paymentMode: "",
  paymentModeOther: "",
  bookingType: "",
  officialAttachment: null,
  roomType: "single",
  roomRequestsByPreference: {},
});

function ApplicationStayForm({
  mode = "readonly",
  onSubmit,
  request,
  submitLabel = "Submit Booking Request",
  guestHouses = [],
  disabled = false,
}) {
  const isEditable = mode === "edit";
  const [values, setValues] = useState(() => getInitialFormValues(request));
  const [attachmentError, setAttachmentError] = useState("");
  const [formError, setFormError] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const [requiredFieldErrors, setRequiredFieldErrors] = useState({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const skipNextAutoSaveRef = useRef(false);
  const [roomTypesByGh, setRoomTypesByGh] = useState({});
  const fetchingGhIdsRef = useRef(new Set());
  const [roomTypesPanelOpen, setRoomTypesPanelOpen] = useState({});
  const [paymentResponsibilityOptionsRaw, setPaymentResponsibilityOptionsRaw] = useState([]);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(true);

  const formSteps = ["Applicant Details", "Guest Information", "Accommodation & Stay Details"];
  const goToNextStep = () => setActiveStep((step) => Math.min(step + 1, formSteps.length - 1));
  const goToPreviousStep = () => setActiveStep((step) => Math.max(step - 1, 0));

  useEffect(() => {
    skipNextAutoSaveRef.current = true;
    setValues(getInitialFormValues(request));
    setAttachmentError("");
    setFormError("");
    setDraftMessage("");
    setRequiredFieldErrors({});
    setFileInputKey((currentKey) => currentKey + 1);
  }, [request]);

  useEffect(() => {
    if (!isEditable) {
      return undefined;
    }

    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return undefined;
    }

    const autoSaveTimer = setTimeout(() => {
      saveDraftValues(values);
    }, 500);

    return () => clearTimeout(autoSaveTimer);
  }, [isEditable, values]);

  const getGuestHouseId = (name) =>
    guestHouses.find((gh) => gh.name === name)?.id;

  /* Fetch room types (publicly, no auth) for every currently-selected
     preference's guest house, caching per guest-house id so re-selecting
     the same guest house doesn't re-fetch. */
  useEffect(() => {
    (values.guestHousePreferences || []).forEach((name) => {
      const ghId = getGuestHouseId(name);
      if (!ghId) return;
      if (roomTypesByGh[ghId] !== undefined) return;
      if (fetchingGhIdsRef.current.has(ghId)) return;

      fetchingGhIdsRef.current.add(ghId);
      roomTypeApi
        .list(ghId)
        .then((types) => setRoomTypesByGh((prev) => ({ ...prev, [ghId]: types })))
        .catch(() => setRoomTypesByGh((prev) => ({ ...prev, [ghId]: [] })))
        .finally(() => fetchingGhIdsRef.current.delete(ghId));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.guestHousePreferences, roomTypesByGh]);

  /* Payment Responsibility options are admin-configurable — fetch the live
     list once (publicly, no auth) rather than hardcoding it. */
  useEffect(() => {
    paymentResponsibilityApi
      .list()
      .then(setPaymentResponsibilityOptionsRaw)
      .catch(() => {})
      .finally(() => setPaymentOptionsLoading(false));
  }, []);

  const isOfficialBooking = values.bookingType === "Other";
  const paymentResponsibilityOptions = paymentResponsibilityOptionsRaw
    .filter((o) => o.is_active && o.booking_type === (isOfficialBooking ? "OFFICIAL" : "NON_OFFICIAL"))
    .map((o) => ({ key: o.code, text: o.label, value: o.code }));
  const hasFieldError = (key) => Boolean(requiredFieldErrors[key]);

  const APPLICANT_TO_GUEST_FIELD = {
    applicantName: "guestName",
    applicantDesignation: "guestDesignation",
    applicantMobileNumber: "guestMobileNumber",
  };

  const updateValue = (key, value) => {
    setFormError("");
    setDraftMessage("");
    setRequiredFieldErrors((currentErrors) => {
      const keysToClear = [key];
      if (key === "guestSameAsApplicant" && value) {
        keysToClear.push(...Object.values(APPLICANT_TO_GUEST_FIELD));
      }
      if (!keysToClear.some((k) => currentErrors[k])) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      keysToClear.forEach((k) => delete nextErrors[k]);
      return nextErrors;
    });

    setValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [key]: value,
      };

      if (key === "bookingType") {
        nextValues.paymentMode = "";
        nextValues.paymentModeOther = "";
      }

      if (
        key === "arrivalDate" || key === "departureDate" ||
        key === "arrivalTime" || key === "departureTime"
      ) {
        nextValues.numberOfDays = getStayDaysFromDates(
          nextValues.arrivalDate,
          nextValues.departureDate,
          nextValues.arrivalTime,
          nextValues.departureTime,
        );
      }

      if (key === "guestSameAsApplicant" && value) {
        Object.entries(APPLICANT_TO_GUEST_FIELD).forEach(([applicantKey, guestKey]) => {
          nextValues[guestKey] = nextValues[applicantKey];
        });
      }

      if (currentValues.guestSameAsApplicant && APPLICANT_TO_GUEST_FIELD[key]) {
        nextValues[APPLICANT_TO_GUEST_FIELD[key]] = value;
      }

      return nextValues;
    });
  };

  const updateGuestHousePreference = (index, guestHouseName) => {
    setFormError("");
    setRequiredFieldErrors((currentErrors) => {
      const errorKey = `guestHousePreference-${index}`;

      if (!currentErrors[errorKey]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[errorKey];
      return nextErrors;
    });

    setValues((currentValues) => {
      const currentPreferences = currentValues.guestHousePreferences || [];
      const nextPreferences = [...currentPreferences];

      nextPreferences[index] = guestHouseName;

      const compactPreferences = nextPreferences
        .filter(Boolean)
        .filter(
          (preference, preferenceIndex, preferences) =>
            preferences.indexOf(preference) === preferenceIndex,
        )
        .slice(0, guestHousePreferenceLimit);

      return {
        ...currentValues,
        guestHouseName: compactPreferences[0] || getFallbackGuestHouseName(),
        guestHousePreferences: compactPreferences,
        roomRequestsByPreference: {
          ...currentValues.roomRequestsByPreference,
          [index]: [],
        },
      };
    });
  };

  const getRoomRequestCount = (slotIndex, roomTypeId) => {
    const entry = (values.roomRequestsByPreference?.[slotIndex] || []).find(
      (r) => r.roomTypeId === roomTypeId,
    );
    return entry ? String(entry.count) : "";
  };

  const getPreferenceRoomTotal = (slotIndex) =>
    (values.roomRequestsByPreference?.[slotIndex] || []).reduce(
      (sum, r) => sum + (Number(r.count) || 0),
      0,
    );

  const updateRoomRequestCount = (slotIndex, roomTypeId, rawValue) => {
    const count = Number(rawValue);
    setValues((currentValues) => {
      const currentList = currentValues.roomRequestsByPreference?.[slotIndex] || [];
      const withoutType = currentList.filter((r) => r.roomTypeId !== roomTypeId);
      const nextList = count > 0 ? [...withoutType, { roomTypeId, count }] : withoutType;

      return {
        ...currentValues,
        roomRequestsByPreference: {
          ...currentValues.roomRequestsByPreference,
          [slotIndex]: nextList,
        },
      };
    });
  };

  const stepRoomRequestCount = (slotIndex, roomTypeId, delta) => {
    const nextCount = Math.max(
      0,
      (Number(getRoomRequestCount(slotIndex, roomTypeId)) || 0) + delta,
    );
    updateRoomRequestCount(slotIndex, roomTypeId, nextCount);
  };

  const toggleRoomTypesPanel = (slotIndex) => {
    setRoomTypesPanelOpen((current) => ({
      ...current,
      [slotIndex]: !current[slotIndex],
    }));
  };

  const getRoomTypeCapacityPriceText = (roomType) => {
    const capacityText = `Capacity: ${roomType.capacity} guest${roomType.capacity === 1 ? "" : "s"}`;
    return roomType.base_price ? `${capacityText} · ₹${roomType.base_price}/night` : capacityText;
  };

  const getInputProps = (key, options = {}) => ({
    ...options,
    fluid: true,
    onChange: (event) => updateValue(key, event.target.value),
    readOnly: !isEditable,
    value: values[key] || "",
  });

  const updateOfficialAttachment = (file) => {
    if (file && file.size > MAX_ATTACHMENT_SIZE) {
      /* bump the key so the rejected file is cleared and the same file can be
         re-picked after the user swaps it */
      setFileInputKey((key) => key + 1);
      setValues((currentValues) => ({ ...currentValues, officialAttachment: null }));
      setAttachmentError(ATTACHMENT_TOO_LARGE_MSG);
      return;
    }

    setValues((currentValues) => ({
      ...currentValues,
      /* store the real File object so it can be sent as multipart/form-data */
      officialAttachment: file || null,
    }));
    setAttachmentError("");
    setFormError("");
    setRequiredFieldErrors((currentErrors) => {
      if (!currentErrors.officialAttachment) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors.officialAttachment;
      return nextErrors;
    });
  };

  const validateOfficialAttachment = () => {
    if (!isOfficialBooking || values.officialAttachment?.name) {
      setAttachmentError("");
      return true;
    }

    setAttachmentError("Attachment is mandatory for official booking.");
    return false;
  };

  const validateRequiredFields = () => {
    const nextErrors = {};
    const missingFields = [];
    const addMissingField = (key, label) => {
      nextErrors[key] = true;
      missingFields.push(label);
    };
    const isEmpty = (value) => !value?.toString().trim();

    [
      ["employeeId", "Employee ID"],
      ["applicantName", "Applicant Name"],
      ["applicantDesignation", "Designation"],
      ["applicantDepartment", "Department"],
      ["emailId", "Email ID"],
      ["applicantMobileNumber", "Mobile No"],
      ["guestName", "Full Name"],
      ["guestMobileNumber", "Mobile Number"],
      ["bookingType", "Booking Type"],
      ["roomCount", "Rooms"],
      ["visitPurpose", "Purpose of Visit"],
      ["arrivalDate", "Arrival"],
      ["arrivalTime", "Arrival Time"],
      ["departureDate", "Departure"],
      ["departureTime", "Departure Time"],
      ["paymentMode", "Payment Responsibility"],
    ].forEach(([key, label]) => {
      if (isEmpty(values[key])) {
        addMissingField(key, label);
      }
    });

    if (!isEmpty(values.roomCount) && Number(values.roomCount) < 1) {
      addMissingField("roomCount", "Rooms");
    }

    guestHousePreferenceSlots.forEach((guestHouseName, index) => {
      if (isEmpty(guestHouseName)) {
        addMissingField(
          `guestHousePreference-${index}`,
          `Preference ${index + 1}`,
        );
      }
    });

    const requiredRoomCount = Number(values.roomCount) || 0;
    guestHousePreferenceSlots.forEach((guestHouseName, index) => {
      if (!guestHouseName || !requiredRoomCount) {
        return;
      }

      if (getPreferenceRoomTotal(index) !== requiredRoomCount) {
        addMissingField(`roomTotal-${index}`, `Preference ${index + 1} Room Selection`);
      }
    });

    if (isOfficialBooking && !values.officialAttachment?.name) {
      addMissingField("officialAttachment", "Official Attachment");
    }

    if (values.paymentMode === OTHER_PAYMENT_CODE && isEmpty(values.paymentModeOther)) {
      addMissingField("paymentModeOther", "Please Specify (Payment Responsibility)");
    }

    /* Dates alone no longer settle the stay: a same-day booking is legal, but
       only the hours separate arrival from departure, so they must be ordered. */
    let dateOrderError = "";
    if (values.arrivalDate && values.departureDate) {
      if (values.departureDate < values.arrivalDate) {
        dateOrderError = "Departure date cannot be before the arrival date.";
      } else if (
        values.departureDate === values.arrivalDate &&
        values.arrivalTime && values.departureTime &&
        values.departureTime <= values.arrivalTime
      ) {
        dateOrderError =
          "Departure time must be after arrival time for a same-day booking.";
      }
    }

    setFormError(
      missingFields.length
        ? "Please fill all required fields before submitting."
        : dateOrderError,
    );

    return missingFields.length === 0 && !dateOrderError;
  };

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleSubmit = () => {
    if (!isEditable) {
      return false;
    }

    const hasRequiredFields = validateRequiredFields();
    const hasOfficialAttachment = validateOfficialAttachment();

    if (!hasRequiredFields || !hasOfficialAttachment) {
      return false;
    }

    const guestHousePreferences = values.guestHousePreferences?.length
      ? values.guestHousePreferences
      : [values.guestHouseName || getFallbackGuestHouseName()];

    onSubmit?.({
      ...values,
      arrivalDate: getIsoDateValue(values.arrivalDate) || values.arrivalDate,
      departureDate:
        getIsoDateValue(values.departureDate) || values.departureDate,
      guestHouseName: guestHousePreferences[0],
      guestHousePreferences,
      officialAttachment: isOfficialBooking ? values.officialAttachment : null,
      accompanyingPersonsCount: Number(values.accompanyingPersonsCount) || 0,
    });

    return true;
  };

  const handleClearForm = () => {
    skipNextAutoSaveRef.current = true;
    setValues(getClearedFormValues());
    setAttachmentError("");
    setFormError("");
    setDraftMessage("");
    setRequiredFieldErrors({});
    setIsPreviewOpen(false);
    setFileInputKey((currentKey) => currentKey + 1);
  };

  const handleSaveDraft = () => {
    const isSaved = saveDraftValues(values);
    setFormError("");
    setDraftMessage(isSaved ? "Draft saved successfully." : "Unable to save draft.");
  };

  const handlePreview = () => {
    const hasRequiredFields = validateRequiredFields();
    const hasOfficialAttachment = validateOfficialAttachment();

    if (!hasRequiredFields || !hasOfficialAttachment) {
      return;
    }

    setIsPreviewOpen(true);
  };

  const handleDownloadPDF = () => {
    const previewContent = document.getElementById("booking-preview-content");
    const printDocumentName = getEmployeeDocumentName(values);

    if (!previewContent) {
      window.print();
      return;
    }

    const printFrame = document.createElement("iframe");
    const removePrintFrame = () => {
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    };

    printFrame.setAttribute("title", printDocumentName);
    printFrame.style.position = "fixed";
    printFrame.style.left = "-10000px";
    printFrame.style.top = "0";
    printFrame.style.width = "794px";
    printFrame.style.height = "1123px";
    printFrame.style.border = "0";
    printFrame.style.opacity = "0";

    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printWindow?.document;

    if (!printWindow || !printDocument) {
      removePrintFrame();
      window.print();
      return;
    }

    printDocument.open();
    printDocument.write(
      getPreviewPrintMarkup(previewContent.outerHTML, printDocumentName),
    );
    printDocument.close();

    const originalDocumentTitle = document.title;
    document.title = printDocumentName;

    printWindow.onafterprint = () => {
      document.title = originalDocumentTitle;
      removePrintFrame();
    };

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        document.title = originalDocumentTitle;
        removePrintFrame();
      }, 1000);
    }, 500);
  };

  const handlePrintPDF = () => {
    handleDownloadPDF();
  };

  const finalSubmit = () => {
    if (handleSubmit()) {
      setIsPreviewOpen(false);
    }
  };

  const previewSections = [
    {
      title: "Applicant Details",
      rows: [
        ["Employee ID", values.employeeId],
        ["Applicant Name", values.applicantName],
        ["Designation", values.applicantDesignation],
        ["Department", values.applicantDepartment],
        ["Email ID", values.emailId],
        ["Mobile No", values.applicantMobileNumber],
      ],
    },
    {
      title: "Guest House Preferences",
      rows: [
        ["Selected Guest House", values.guestHouseName],
        ["All Preferences", values.guestHousePreferences],
        ["Number of Rooms", values.roomCount],
      ],
    },
    {
      title: "Guest Information",
      rows: [
        ["Guest Name", values.guestName],
        ["Guest Designation", values.guestDesignation],
        ["Guest Mobile Number", values.guestMobileNumber],
        ["Booking Type", getBookingTypeLabel(values.bookingType)],
        ...(isOfficialBooking
          ? [["Official Attachment", values.officialAttachment?.name]]
          : []),
        ["Residential Address", values.guestAddress],
        ["Purpose of Visit", values.visitPurpose],
      ],
    },
    {
      title: "Stay Duration",
      rows: [
        ["Arrival Date", getDisplayDateValue(values.arrivalDate)],
        ["Arrival Time", getDisplayTimeValue(values.arrivalTime)],
        ["Departure Date", getDisplayDateValue(values.departureDate)],
        ["Departure Time", getDisplayTimeValue(values.departureTime)],
        ["No. of Days/Nights", values.numberOfDays],
      ],
    },
    {
      title: "Payment",
      rows: [
        [
          "Payment Responsibility",
          paymentResponsibilityOptions.find((o) => o.value === values.paymentMode)?.text || values.paymentMode,
        ],
        ...(values.paymentMode === OTHER_PAYMENT_CODE
          ? [["Please Specify", values.paymentModeOther]]
          : []),
      ],
    },
  ];

  const selectedGuestHousePreferences = values.guestHousePreferences || [];
  const guestHousePreferenceSlots = Array.from(
    { length: guestHousePreferenceLimit },
    (_, index) => selectedGuestHousePreferences[index] || "",
  );

  return (
    <Segment basic style={{ padding: "1.5rem 1.25rem" }} aria-label={formData.header.title}>
      <Segment style={{ maxWidth: 1100, margin: "0 auto", padding: 0, overflow: "hidden" }}>
        <Segment
          inverted
          style={{
            margin: 0,
            borderRadius: 0,
            background: "linear-gradient(135deg, #143a5a 0%, #1c5080 100%)",
          }}
        >
          <Header inverted as="h2" style={{ margin: 0 }}>
            <Icon name="building outline" />
            <Header.Content>Guest House Accommodation Request Form</Header.Content>
          </Header>
        </Segment>

        <Step.Group widths={3} fluid style={{ margin: 0, borderRadius: 0, boxShadow: "none" }}>
          {formSteps.map((label, index) => (
            <Step
              key={label}
              link
              active={activeStep === index}
              completed={activeStep > index}
              onClick={() => setActiveStep(index)}
            >
              <Icon name={STEP_ICONS[index]} />
              <Step.Content>
                <Step.Title>{label}</Step.Title>
              </Step.Content>
            </Step>
          ))}
        </Step.Group>

        <Form style={{ padding: "20px 24px" }} autoComplete="off">
          {activeStep === 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
                <Header as="h4" style={{ margin: 0 }}>Applicant Details</Header>
                <span style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.6)" }}>
                  All fields marked <strong style={{ color: "#db2828" }}>*</strong> are required
                </span>
              </div>
              <Form.Group widths="equal">
                <Form.Input
                  label="Employee ID"
                  required
                  error={hasFieldError("employeeId")}
                  placeholder="Employee ID"
                  {...getInputProps("employeeId")}
                />
                <Form.Input
                  label="Applicant Name"
                  required
                  error={hasFieldError("applicantName")}
                  placeholder="Applicant Name"
                  {...getInputProps("applicantName")}
                />
                <Form.Input
                  label="Designation"
                  required
                  error={hasFieldError("applicantDesignation")}
                  placeholder="Designation"
                  {...getInputProps("applicantDesignation")}
                />
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Input
                  label="Department"
                  required
                  error={hasFieldError("applicantDepartment")}
                  placeholder="Department"
                  {...getInputProps("applicantDepartment")}
                />
                <Form.Input
                  label="Email ID"
                  required
                  error={hasFieldError("emailId")}
                  placeholder="name@example.com"
                  {...getInputProps("emailId", { type: "email" })}
                />
                <Form.Input
                  label="Mobile No"
                  required
                  error={hasFieldError("applicantMobileNumber")}
                  placeholder="Mobile Number"
                  {...getInputProps("applicantMobileNumber", { type: "tel" })}
                />
              </Form.Group>
            </>
          )}

          {activeStep === 1 && (
            <>
              <Header as="h4">Visit Details</Header>
              <Form.Group widths="equal">
                <Form.Select
                  label="Booking Type"
                  required
                  error={hasFieldError("bookingType")}
                  disabled={!isEditable}
                  options={bookingTypeOptions}
                  placeholder="Select Booking Type"
                  value={values.bookingType}
                  onChange={(_, data) => {
                    setAttachmentError("");
                    updateValue("bookingType", data.value?.toString() || "");
                  }}
                />
                <Form.Input
                  label="Purpose of Visit"
                  required
                  error={hasFieldError("visitPurpose")}
                  placeholder="Purpose of Visit"
                  {...getInputProps("visitPurpose")}
                />
                <Form.Select
                  label="Payment Responsibility"
                  required
                  error={hasFieldError("paymentMode")}
                  clearable
                  disabled={!isEditable}
                  loading={paymentOptionsLoading}
                  options={paymentResponsibilityOptions}
                  placeholder="Select Payment Responsibility"
                  value={values.paymentMode}
                  onChange={(_, data) => updateValue("paymentMode", data.value?.toString() || "")}
                />
              </Form.Group>

              {values.paymentMode === OTHER_PAYMENT_CODE && (
                <Form.Group widths="equal">
                  <Form.Input
                    label="Please Specify"
                    required
                    error={hasFieldError("paymentModeOther")}
                    disabled={!isEditable}
                    placeholder="Who is responsible for payment?"
                    {...getInputProps("paymentModeOther")}
                  />
                </Form.Group>
              )}

              {isOfficialBooking && (
                <Form.Field required error={hasFieldError("officialAttachment")}>
                  <label>Official Attachment</label>
                  {isEditable ? (
                    <>
                      <Button as="label" htmlFor={`official-file-${fileInputKey}`} basic type="button">
                        <Icon name="paperclip" />
                        {values.officialAttachment?.name || "Choose file…"}
                      </Button>
                      <input
                        key={fileInputKey}
                        id={`official-file-${fileInputKey}`}
                        type="file"
                        hidden
                        onChange={(event) =>
                          updateOfficialAttachment(event.target.files?.[0] || null)
                        }
                      />
                    </>
                  ) : (
                    <span>{values.officialAttachment?.name || "-"}</span>
                  )}
                  {attachmentError && (
                    <Message negative size="small" style={{ marginTop: 8 }} content={attachmentError} />
                  )}
                </Form.Field>
              )}

              <Divider />

              <Header as="h4">Guest Information</Header>
              <Form.Field>
                <Checkbox
                  label="Guest information same as applicant"
                  checked={values.guestSameAsApplicant}
                  disabled={!isEditable}
                  onChange={(_, data) => updateValue("guestSameAsApplicant", !!data.checked)}
                />
              </Form.Field>
              <Form.Group widths="equal">
                <Form.Input
                  label="Guest Name"
                  required
                  error={hasFieldError("guestName")}
                  placeholder="Full Name"
                  {...getInputProps("guestName")}
                  readOnly={!isEditable || values.guestSameAsApplicant}
                  input={values.guestSameAsApplicant ? { style: GREYED_INPUT_STYLE } : undefined}
                />
                <Form.Input
                  label="Guest Designation"
                  error={hasFieldError("guestDesignation")}
                  placeholder="Designation"
                  {...getInputProps("guestDesignation")}
                  readOnly={!isEditable || values.guestSameAsApplicant}
                  input={values.guestSameAsApplicant ? { style: GREYED_INPUT_STYLE } : undefined}
                />
                <Form.Input
                  label="Mobile Number"
                  required
                  error={hasFieldError("guestMobileNumber")}
                  placeholder="Mobile Number"
                  {...getInputProps("guestMobileNumber", { type: "tel" })}
                  readOnly={!isEditable || values.guestSameAsApplicant}
                  input={values.guestSameAsApplicant ? { style: GREYED_INPUT_STYLE } : undefined}
                />
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Input
                  label="Address"
                  error={hasFieldError("guestAddress")}
                  placeholder="Street Address"
                  {...getInputProps("guestAddress")}
                />
                <Form.Input
                  label="No. of Accompanying Persons"
                  icon="user"
                  iconPosition="left"
                  placeholder="0"
                  readOnly={!isEditable}
                  {...getInputProps("accompanyingPersonsCount", { type: "number", min: "0" })}
                />
              </Form.Group>
            </>
          )}

          {activeStep === 2 && (
            <>
              <Header as="h4">Accommodation Details</Header>
              <Form.Group widths="equal">
                <Form.Input
                  label="Rooms Required"
                  required
                  error={hasFieldError("roomCount")}
                  placeholder="Rooms"
                  {...getInputProps("roomCount", { type: "number", min: "1" })}
                />
              </Form.Group>

              <Divider />

              <Form.Group widths="equal">
                {guestHousePreferenceSlots.map((guestHouseName, index) => {
                  const slotOptions = guestHouseDropdownOptions.map((option) => ({
                    ...option,
                    disabled:
                      option.value !== guestHouseName &&
                      selectedGuestHousePreferences.includes(option.value),
                  }));

                  return (
                    <Form.Select
                      key={index}
                      label={`Preference ${index + 1}`}
                      required
                      error={hasFieldError(`guestHousePreference-${index}`)}
                      clearable
                      disabled={!isEditable}
                      options={slotOptions}
                      placeholder="Select Guest House"
                      search
                      value={guestHouseName}
                      onChange={(_, data) =>
                        updateGuestHousePreference(index, data.value?.toString() || "")
                      }
                    />
                  );
                })}
              </Form.Group>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 14,
                }}
              >
              {guestHousePreferenceSlots.map((guestHouseName, index) => {
                if (!guestHouseName) {
                  return <div key={`room-types-${index}`} />;
                }

                const ghId = getGuestHouseId(guestHouseName);
                if (!ghId) {
                  return <div key={`room-types-${index}`} />;
                }

                const roomTypesForGh = roomTypesByGh[ghId];
                const isPanelOpen = Boolean(roomTypesPanelOpen[index]);
                const selectedRoomRequests = (values.roomRequestsByPreference?.[index] || [])
                  .filter((r) => Number(r.count) > 0);
                const selectedSummary = selectedRoomRequests
                  .map((r) => {
                    const roomTypeName = (roomTypesForGh || []).find((rt) => rt.id === r.roomTypeId)?.name;
                    return roomTypeName ? `${r.count} ${roomTypeName}` : null;
                  })
                  .filter(Boolean)
                  .join(", ");

                return (
                  <div key={`room-types-${index}`} style={{ margin: "4px 0 16px" }}>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        color: "rgba(0,0,0,0.5)",
                        marginBottom: 6,
                      }}
                    >
                      Room types for {guestHouseName}
                    </div>

                    <div
                      style={{
                        border: "1px solid rgba(34,36,38,.15)",
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#fff",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleRoomTypesPanel(index)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 16px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#143a5a",
                          textAlign: "left",
                        }}
                      >
                        <span>{selectedSummary || "Select room types"}</span>
                        <Icon name={isPanelOpen ? "chevron up" : "chevron down"} />
                      </button>

                      {isPanelOpen && (
                        <div style={{ borderTop: "1px solid rgba(34,36,38,.1)" }}>
                          {roomTypesForGh === undefined ? (
                            <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                              <Icon name="spinner" loading /> Loading room types…
                            </p>
                          ) : roomTypesForGh.length === 0 ? (
                            <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                              <em>No room types configured for this guest house.</em>
                            </p>
                          ) : (
                            roomTypesForGh.map((roomType, roomTypeIndex) => {
                              const count = Number(getRoomRequestCount(index, roomType.id)) || 0;
                              return (
                                <div
                                  key={roomType.id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "14px 16px",
                                    borderTop: roomTypeIndex > 0 ? "1px solid rgba(34,36,38,.08)" : "none",
                                  }}
                                >
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{roomType.name}</div>
                                    <div style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.55)" }}>
                                      {getRoomTypeCapacityPriceText(roomType)}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <Button
                                      circular
                                      basic
                                      icon="minus"
                                      size="mini"
                                      type="button"
                                      disabled={!isEditable || count <= 0}
                                      aria-label={`Decrease ${roomType.name} count`}
                                      onClick={() => stepRoomRequestCount(index, roomType.id, -1)}
                                    />
                                    <span style={{ minWidth: 18, textAlign: "center" }}>{count}</span>
                                    <Button
                                      circular
                                      color="blue"
                                      icon="plus"
                                      size="mini"
                                      type="button"
                                      disabled={
                                        !isEditable ||
                                        getPreferenceRoomTotal(index) >= (Number(values.roomCount) || 0)
                                      }
                                      aria-label={`Increase ${roomType.name} count`}
                                      onClick={() => stepRoomRequestCount(index, roomType.id, 1)}
                                    />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    {Boolean(values.roomCount) &&
                      getPreferenceRoomTotal(index) !== Number(values.roomCount) && (
                        <div style={{ color: "#db2828", fontSize: "0.8rem", marginTop: 6 }}>
                          Selected rooms ({getPreferenceRoomTotal(index)}) must equal Rooms
                          Required ({values.roomCount}).
                        </div>
                      )}
                  </div>
                );
              })}
              </div>

              <Header as="h4" style={{ marginTop: 24 }}>Stay Information</Header>
              <Form.Group widths="equal">
                <Form.Input
                  label="Arrival Date"
                  required
                  type="date"
                  error={hasFieldError("arrivalDate")}
                  disabled={!isEditable}
                  value={getIsoDateValue(values.arrivalDate)}
                  onChange={(e) => updateValue("arrivalDate", e.target.value)}
                />
                <TimeInput12h
                  label="Arrival Time"
                  required
                  value={values.arrivalTime || ""}
                  onChange={(v) => updateValue("arrivalTime", v)}
                  disabled={!isEditable}
                  error={hasFieldError("arrivalTime")}
                  ariaLabel="Choose arrival time"
                />
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Input
                  label="Departure Date"
                  required
                  type="date"
                  error={hasFieldError("departureDate")}
                  disabled={!isEditable}
                  value={getIsoDateValue(values.departureDate)}
                  onChange={(e) => updateValue("departureDate", e.target.value)}
                />
                <TimeInput12h
                  label="Departure Time"
                  required
                  value={values.departureTime || ""}
                  onChange={(v) => updateValue("departureTime", v)}
                  disabled={!isEditable}
                  error={hasFieldError("departureTime")}
                  ariaLabel="Choose departure time"
                />
              </Form.Group>

              <Form.Input
                label="No. of Days/Nights"
                fluid
                readOnly
                value={values.numberOfDays || ""}
                placeholder="Auto calculated"
              />
            </>
          )}

          {formError && (
            <Message negative style={{ marginTop: 20 }} role="alert">
              {formError}
            </Message>
          )}
          {draftMessage && (
            <Message positive style={{ marginTop: 20 }} role="status">
              {draftMessage}
            </Message>
          )}

          {isEditable && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 24,
                paddingTop: 16,
                borderTop: "1px solid rgba(34,36,38,.15)",
              }}
            >
              <Button basic onClick={handleClearForm} type="button">
                Clear
              </Button>
              {activeStep > 0 && (
                <Button basic onClick={goToPreviousStep} type="button">
                  <Icon name="arrow left" /> Back
                </Button>
              )}
              <Button basic color="blue" onClick={handleSaveDraft} type="button">
                <Icon name="save" /> Save
              </Button>
              {activeStep < formSteps.length - 1 ? (
                <Button primary onClick={goToNextStep} type="button">
                  Next <Icon name="arrow right" />
                </Button>
              ) : (
                <>
                  <Button color="blue" basic onClick={handlePreview} type="button" disabled={disabled}>
                    <Icon name="eye" /> Preview
                  </Button>
                  <Button primary onClick={handleSubmit} type="button" loading={disabled} disabled={disabled}>
                    Submit Application
                  </Button>
                </>
              )}
            </div>
          )}
        </Form>
      </Segment>

      {/* Preview Modal */}
      <Modal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="large" closeIcon>
        <Modal.Header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #143a5a 0%, #1c5080 100%)",
            color: "#fff",
          }}
        >
          <span>
            <Icon name="file alternate outline" /> Application Preview
          </span>
          <div>
            <Button size="small" inverted onClick={handleDownloadPDF} type="button">
              <Icon name="download" /> PDF
            </Button>
            <Button size="small" inverted basic onClick={handlePrintPDF} type="button">
              <Icon name="print" /> Print
            </Button>
          </div>
        </Modal.Header>

        <Modal.Content scrolling>
          <div id="booking-preview-content">
            <Header as="h3" textAlign="center" content="Guest House Accommodation Request Form" />

            {previewSections.map((section, sectionIndex) => (
              <Segment key={section.title}>
                <Header as="h5">
                  <Label circular color="blue">
                    {sectionIndex + 1}
                  </Label>{" "}
                  {section.title}
                </Header>
                <Grid columns={2} stackable>
                  {section.rows.map(([label, value]) => (
                    <Grid.Column key={label}>
                      <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.5)", textTransform: "uppercase" }}>
                        {label}
                      </div>
                      <div>{getPreviewValue(value)}</div>
                    </Grid.Column>
                  ))}
                </Grid>
              </Segment>
            ))}

            <Segment>
              <Header as="h5">
                <Label circular color="blue">
                  {previewSections.length + 1}
                </Label>{" "}
                Accompanying Persons
              </Header>
              <p>{Number(values.accompanyingPersonsCount) || 0}</p>
            </Segment>
          </div>
        </Modal.Content>

        <Modal.Actions>
          <Button basic onClick={() => setIsPreviewOpen(false)} type="button" disabled={disabled}>
            <Icon name="arrow left" /> Back to Form
          </Button>
          <Button primary onClick={finalSubmit} type="button" loading={disabled} disabled={disabled}>
            <Icon name="check" /> Confirm &amp; Submit
          </Button>
        </Modal.Actions>
      </Modal>
    </Segment>
  );
}

export default ApplicationStayForm;
